import axios from 'axios';
import { API_BASE_URL_V1, API_BASE_URL_ADMIN } from '../config';

const TOKEN_EXPIRY_KEY = 'tokenExpiry';

const api = axios.create({
    baseURL: API_BASE_URL_V1,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Helper function to set token with expiry
const setTokenWithExpiry = (token, expiresIn = 24 * 60 * 60 * 1000) => { // Default 24 hours
    const expiryTime = new Date().getTime() + expiresIn;
    localStorage.setItem('token', token);
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
};

// Helper function to check if token is expired
const isTokenExpired = () => {
    const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiryTime) return true;
    return new Date().getTime() > parseInt(expiryTime);
};

// Add token to requests if it exists and is not expired
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token && !isTokenExpired()) {
        config.headers.Authorization = `Bearer ${token}`;
    } else {
        // Clear expired token
        localStorage.removeItem('token');
        localStorage.removeItem('authToken');
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
    }
    return config;
});

// Add a response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect for auth-related endpoints
    const authEndpoints = ['/user/check', '/otp/verify', '/otp/send', '/user/onboard'];
    const isAuthEndpoint = authEndpoints.some(endpoint => 
      error.config.url.includes(endpoint)
    );

    if (error.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem('token');
      localStorage.removeItem('authToken');
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authService = {
    setToken: (token, expiresIn = 24 * 60 * 60 * 1000) => {
        setTokenWithExpiry(token, expiresIn);
    },

    checkUser: async (phoneNumber) => {
        try {
            const response = await api.post('/user/check', { phoneNumber });
            return response.data;
        } catch (error) {
            // For check user endpoint, we want to handle the error response as a normal response
            if (error.response?.config?.url?.includes('/user/check')) {
                return {
                    success: false,
                    message: error.response?.data?.message || 'User not found. Please proceed with signup.',
                    data: null,
                    code: error.response?.status
                };
            }
            throw error.response?.data || { message: 'Failed to check user status' };
        }
    },

    verifyOtp: async (phoneNumber, otp) => {
        try {
            const response = await api.post('/otp/verify', { phoneNumber, otp });
            console.log('OTP Verification Response:', response.data); // Debug log
            if (response.data.success && response.data.data) {
                // In verify OTP, the token is directly in data as a string
                authService.setToken(response.data.data);
            }
            return response.data;
        } catch (error) {
            // Handle error response for OTP verification
            if (error.response?.config?.url?.includes('/otp/verify')) {
                return {
                    success: false,
                    message: error.response?.data?.message || 'Failed to verify OTP',
                    data: null,
                    code: error.response?.status
                };
            }
            throw error.response?.data || { message: 'Failed to verify OTP' };
        }
    },

    resendOtp: async (phoneNumber) => {
        try {
            const response = await api.post('/otp/send', { phoneNumber });
            return response.data;
        } catch (error) {
            // Handle error response for OTP resend
            if (error.response?.config?.url?.includes('/otp/send')) {
                return {
                    success: false,
                    message: error.response?.data?.message || 'Failed to resend OTP',
                    data: null,
                    code: error.response?.status
                };
            }
            throw error.response?.data || { message: 'Failed to resend OTP' };
        }
    },

    signup: async (userData) => {
        try {
            const response = await api.post('/user/onboard', userData);
            console.log('Signup Response:', response.data); // Debug log
            if (response.data.success && response.data.data?.token) {
                // In signup, the token is nested in data.token
                authService.setToken(response.data.data.token);
            }
            return response.data;
        } catch (error) {
            // Handle error response for signup
            if (error.response?.config?.url?.includes('/user/onboard')) {
                return {
                    success: false,
                    message: error.response?.data?.message || 'Failed to sign up',
                    data: null,
                    code: error.response?.status
                };
            }
            throw error.response?.data || { message: 'Failed to sign up' };
        }
    },

    logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('authToken');
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
    },

    isAuthenticated: () => {
        const token = localStorage.getItem('token');
        return !!token && !isTokenExpired();
    }
};

export const userAPI = {
  getProfile: async () => {
    const response = await api.get('/user/profile');
    return response.data;
  },
  updateProfile: async (data) => {
    const response = await api.put('/user/profile', data);
    return response.data;
  },
};



// Clinic APIs
export const getClinicsByCity = async (cityId) => {
    try {
        console.log('API Request cityId:', cityId); // Debug log
        const response = await api.post(`/clinics/discover/all`, 
            cityId
        );
        console.log('Raw API Response:', response); // Debug log
        console.log('Response data:', response.data); // Debug log
        
        // Extract the clinics array from the nested response structure
        const clinicsData = response.data.data || response.data;
        console.log('Extracted clinics data:', clinicsData);
        
        return { success: true, data: clinicsData };
    } catch (error) {
        console.error('Error fetching clinics:', error);
        return { success: false, error: error.response?.data?.message || 'Failed to fetch clinics' };
    }
};

export const getClinicDetails = async (clinicId) => {
    try {
        const response = await api.get(`/clinic/${clinicId}`);
        return response.data; // Return the full response data structure
    } catch (error) {
        console.error('Error fetching clinic details:', error);
        return { 
            success: false, 
            message: error.response?.data?.message || 'Failed to get clinic details',
            code: error.response?.status || 500
        };
    }
};

export const onboardClinic = async (clinicData) => {
    try {
        const response = await api.post(`${API_BASE_URL_ADMIN}/clinic-onboard/create`, {
            name: clinicData.name,
            address: clinicData.address,
            cityId: clinicData.cityId,
            stateId: clinicData.stateId,
            pincode: clinicData.pincode,
            latitude: clinicData.latitude,
            longitude: clinicData.longitude,
            phoneNumber: clinicData.phoneNumber,
            email: clinicData.email,
            description: clinicData.description || '',
            clinicOpenTime: clinicData.clinicOpenTime,
            clinicCloseTime: clinicData.clinicCloseTime,
            daysOpen: clinicData.daysOpen || 'Monday,Tuesday,Wednesday,Thursday,Friday,Saturday',
            clinicEstablishedDate: clinicData.establishedDate || new Date().toISOString().split('T')[0],
            totalExperience: clinicData.totalExperience || 0,
            googleMapUrl: clinicData.googleMapUrl || '',
            averageRating: clinicData.averageRating || 0,
            areaId: clinicData.areaId
        });
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error onboarding clinic:', error);
        return { success: false, error: error.response?.data?.message || 'Failed to onboard clinic' };
    }
};

export const updateClinic = async (clinicId, clinicData) => {
    try {
        const response = await api.put(`${API_BASE_URL_ADMIN}/clinic-onboard/update`, {
            ...clinicData,
            clinicId
        });
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error updating clinic:', error);
        return { success: false, error: error.response?.data?.message || 'Failed to update clinic' };
    }
};

export const deactivateClinic = async (clinicId) => {
    try {
        const response = await api.put(`${API_BASE_URL_ADMIN}/clinic-onboard/update`, {
            clinicId,
            status: "INACTIVE"
        });
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error deactivating clinic:', error);
        return { success: false, error: error.response?.data?.message || 'Failed to deactivate clinic' };
    }
};

// Location Service
export const locationService = {
    getStates: async () => {
        try {
            const response = await api.get('/locations/states');
            return response.data;
        } catch (error) {
            console.error('Error fetching states:', error);
            throw error;
        }
    },

    getCities: async (stateId) => {
        try {
            const response = await api.get(`/locations/states/${stateId}/cities`);
            return response.data;
        } catch (error) {
            console.error('Error fetching cities:', error);
            throw error;
        }
    },

    getAreas: async (cityId) => {
        try {
            const response = await api.get(`/cities/${cityId}/areas`);
            return response.data;
        } catch (error) {
            console.error('Error fetching areas:', error);
            throw error;
        }
    }
};

export default api;

/**
 * Get doctors by clinic ID
 * @param {number} clinicId - The clinic ID
 * @returns {Promise} - API response
 */
export const getDoctorsByClinic = async (clinicId) => {
    try {
        const response = await api.get(`${API_BASE_URL_V1}/doctors/clinic/${clinicId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching doctors:', error);
        return { success: false, error: error.response?.data?.message || 'Failed to fetch doctors' };
    }
};

/**
 * Update doctor profile
 * @param {number} doctorId - The doctor profile ID
 * @param {Object} data - The doctor profile data
 * @returns {Promise} - API response
 */
export const updateDoctorProfile = async (doctorId, data) => {
    try {
        const response = await api.put(`${API_BASE_URL_V1}/doctors/profile`, data);
        return response.data;
    } catch (error) {
        console.error('Error updating doctor profile:', error);
        return { success: false, error: error.response?.data?.message || 'Failed to update doctor profile' };
    }
};

/**
 * Update doctor status
 * @param {number} doctorId - The doctor profile ID
 * @param {Object} data - Contains doctorUserId, clinicId, and status
 * @returns {Promise} - API response
 */
export const updateDoctorStatus = async (doctorId, data) => {
    try {
        const response = await api.put(`${API_BASE_URL_V1}/doctors/profile`, {
            doctorUserId: data.doctorUserId,
            clinicId: data.clinicId,
            status: data.status
        });
        return response.data;
    } catch (error) {
        console.error('Error updating doctor status:', error);
        return { success: false, error: error.response?.data?.message || 'Failed to update doctor status' };
    }
};

/**
 * Qualification APIs
 */
export const addDoctorQualification = async (doctorId, qualification) => {
    try {
        const response = await api.post(`/doctors/${doctorId}/qualifications`, qualification);
        return response.data;
    } catch (error) {
        console.error('Error adding qualification:', error);
        return { success: false, error: error.response?.data?.message || 'Failed to add qualification' };
    }
};

export const updateDoctorQualification = async (doctorId, qualificationId, qualification) => {
    try {
        const response = await api.put(`/doctors/${doctorId}/qualifications/${qualificationId}`, qualification);
        return response.data;
    } catch (error) {
        console.error('Error updating qualification:', error);
        return { success: false, error: error.response?.data?.message || 'Failed to update qualification' };
    }
};

export const deleteDoctorQualification = async (doctorId, qualificationId) => {
    try {
        const response = await api.delete(`/doctors/${doctorId}/qualifications/${qualificationId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting qualification:', error);
        return { success: false, error: error.response?.data?.message || 'Failed to delete qualification' };
    }
};

/**
 * Achievement APIs
 */
export const addDoctorAchievement = async (doctorId, achievement) => {
    try {
        const response = await api.post(`/doctors/${doctorId}/achievements`, achievement);
        return response.data;
    } catch (error) {
        console.error('Error adding achievement:', error);
        return { success: false, error: error.response?.data?.message || 'Failed to add achievement' };
    }
};

export const updateDoctorAchievement = async (doctorId, achievementId, achievement) => {
    try {
        const response = await api.put(`/doctors/${doctorId}/achievements/${achievementId}`, achievement);
        return response.data;
    } catch (error) {
        console.error('Error updating achievement:', error);
        return { success: false, error: error.response?.data?.message || 'Failed to update achievement' };
    }
};

export const deleteDoctorAchievement = async (doctorId, achievementId) => {
    try {
        const response = await api.delete(`/doctors/${doctorId}/achievements/${achievementId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting achievement:', error);
        return { success: false, error: error.response?.data?.message || 'Failed to delete achievement' };
    }
};

/**
 * Certification APIs
 */
export const addDoctorCertification = async (doctorId, certification) => {
    try {
        const response = await api.post(`/doctors/${doctorId}/certifications`, certification);
        return response.data;
    } catch (error) {
        console.error('Error adding certification:', error);
        return { success: false, error: error.response?.data?.message || 'Failed to add certification' };
    }
};

export const updateDoctorCertification = async (doctorId, certificationId, certification) => {
    try {
        const response = await api.put(`/doctors/${doctorId}/certifications/${certificationId}`, certification);
        return response.data;
    } catch (error) {
        console.error('Error updating certification:', error);
        return { success: false, error: error.response?.data?.message || 'Failed to update certification' };
    }
};

export const deleteDoctorCertification = async (doctorId, certificationId) => {
    try {
        const response = await api.delete(`/doctors/${doctorId}/certifications/${certificationId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting certification:', error);
        return { success: false, error: error.response?.data?.message || 'Failed to delete certification' };
    }
};

/**
 * Upload doctor profile image
 * @param {number} clinicId - The clinic ID
 * @param {number} doctorUserId - The doctor's user ID
 * @param {number} doctorId - The doctor profile ID
 * @param {File} image - The image file to upload
 * @returns {Promise} - API response
 */
export const uploadDoctorProfileImage = async (clinicId, doctorUserId, doctorId, image) => {
    try {
        const formData = new FormData();
        formData.append('clinicId', clinicId);
        formData.append('userId', doctorUserId);
        formData.append('doctorId', doctorId);
        formData.append('image', image);
        formData.append('description', 'Doctor Profile Image');
        formData.append('imageType', 'DOCTOR');

        const response = await api.post('/doctor-images/upload', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading doctor profile image:', error);
        return { success: false, error: error.response?.data?.message || 'Failed to upload profile image' };
    }
};

/**
 * Create doctor profile
 * @param {Object} doctorData - The doctor profile data
 * @returns {Promise} - API response
 */
export const createDoctorProfile = async (doctorData) => {
    try {
        const response = await api.post('/doctors/profile', doctorData);
        return response.data;
    } catch (error) {
        console.error('Error creating doctor profile:', error);
        return { 
            success: false, 
            message: error.response?.data?.message || 'Failed to create doctor profile',
            code: error.response?.status || 500
        };
    }
};

// Clinic Images APIs
export const getClinicImages = async (clinicId) => {
    try {
        const response = await api.get(`/clinic-images/clinic/${clinicId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching clinic images:', error);
        return { 
            success: false, 
            message: error.response?.data?.message || 'Failed to fetch clinic images',
            code: error.response?.status || 500
        };
    }
};

export const uploadClinicImage = async (clinicId, imageData) => {
    try {
        const formData = new FormData();
        formData.append('clinicId', clinicId);
        formData.append('image', imageData.image);
        formData.append('description', imageData.description);
        formData.append('imageType', imageData.imageType);

        const response = await api.post(`/clinic-images/upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading clinic image:', error);
        return { 
            success: false, 
            message: error.response?.data?.message || 'Failed to upload clinic image',
            code: error.response?.status || 500
        };
    }
};

export const deleteClinicImage = async (clinicId, imageUrl, imageType) => {
    try {
        const response = await api.delete('/clinic-images/clinic/image', {
            data: {
                clinicId: clinicId,
                url: imageUrl,
                imageType: imageType
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error deleting clinic image:', error);
        return { 
            success: false, 
            message: error.response?.data?.message || 'Failed to delete clinic image',
            code: error.response?.status || 500
        };
    }
};

// Services APIs
export const getAllServices = async () => {
    try {
        const response = await api.get('/services');
        return response.data;
    } catch (error) {
        console.error('Error fetching services:', error);
        return { 
            success: false, 
            message: error.response?.data?.message || 'Failed to fetch services',
            code: error.response?.status || 500
        };
    }
};

export const createService = async (serviceData) => {
    try {
        const response = await api.post('/services', serviceData);
        return response.data;
    } catch (error) {
        console.error('Error creating service:', error);
        return { 
            success: false, 
            message: error.response?.data?.message || 'Failed to create service',
            code: error.response?.status || 500
        };
    }
};

export const addClinicService = async (clinicServiceData) => {
    try {
        const response = await api.post('/clinic-services', clinicServiceData);
        return response.data;
    } catch (error) {
        console.error('Error adding clinic service:', error);
        return { 
            success: false, 
            message: error.response?.data?.message || 'Failed to add clinic service',
            code: error.response?.status || 500
        };
    }
};

export const getClinicServices = async (clinicId) => {
    try {
        const response = await api.get(`/clinic-services/clinic/${clinicId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching clinic services:', error);
        return { 
            success: false, 
            message: error.response?.data?.message || 'Failed to fetch clinic services',
            code: error.response?.status || 500
        };
    }
};

export const deleteClinicService = async (clinicServiceId) => {
    try {
        const response = await api.delete(`/clinic-services/${clinicServiceId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting clinic service:', error);
        return { 
            success: false, 
            message: error.response?.data?.message || 'Failed to delete clinic service',
            code: error.response?.status || 500
        };
    }
};

export const getClinicServiceById = async (clinicServiceId) => {
    try {
        const response = await api.get(`/clinic-services/${clinicServiceId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching clinic service:', error);
        return { 
            success: false, 
            message: error.response?.data?.message || 'Failed to fetch clinic service',
            code: error.response?.status || 500
        };
    }
};

export const updateClinicService = async (clinicServiceId, updateData) => {
    try {
        const response = await api.put(`/clinic-services/${clinicServiceId}`, updateData);
        return response.data;
    } catch (error) {
        console.error('Error updating clinic service:', error);
        return { 
            success: false, 
            message: error.response?.data?.message || 'Failed to update clinic service',
            code: error.response?.status || 500
        };
    }
};

// Before/After Images APIs
export const getBeforeAfterImages = async (clinicServiceId) => {
    try {
        const response = await api.get(`/before-after-images/clinic-service/${clinicServiceId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching before/after images:', error);
        return { 
            success: false, 
            message: error.response?.data?.message || 'Failed to fetch before/after images',
            code: error.response?.status || 500
        };
    }
};

export const uploadBeforeAfterImage = async (formData) => {
    try {
        const response = await api.post('/before-after-images', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading before/after image:', error);
        return { 
            success: false, 
            message: error.response?.data?.message || 'Failed to upload before/after image',
            code: error.response?.status || 500
        };
    }
};

export const deleteBeforeAfterImage = async (imageId) => {
    try {
        const response = await api.delete(`/before-after-images/${imageId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting before/after image:', error);
        return { 
            success: false, 
            message: error.response?.data?.message || 'Failed to delete before/after image',
            code: error.response?.status || 500
        };
    }
}; 